/**
 * Pointer-only shader surface. The permanent halftone lives in
 * halftoneShader.ts; this module owns deformation and the quieter particles
 * that peel from valid strand cells while the pointer field is active.
 */
export const AURORA_INTERACTION_UNIFORMS = `
uniform vec2 uCssResolution;
uniform float uShapeType;
uniform vec2 uPointerPx;
uniform vec2 uPointerVelocity;
uniform float uPointerStrength;
uniform float uPointerStrengthScale;
uniform float uPointerFunnelStrengthScale;
uniform float uPointerRadiusPx;
uniform float uPointerFeather;
uniform float uPointerCoreExclusion;
uniform float uPointerMaxMagnification;
uniform float uPointerDotMagnificationGain;
uniform float uPointerSpreadPx;
uniform float uPointerVelocityBiasPx;
uniform float uEmissionOccupancy;
uniform float uEmissionRadiusScale;
uniform float uEmissionSoftnessScale;
uniform vec2 uEmissionPeelPx;
uniform vec2 uEmissionLifetime;
uniform float uEmissionOpacity;
uniform float uEmissionSeed;
`;
export const AURORA_INTERACTION_HELPERS = `
float pointerLensMagnification(float field) {
  return mix(
    1.0,
    max(uPointerMaxMagnification, 1.0),
    clamp(field, 0.0, 1.0)
  );
}

vec2 pointerDeform(
  vec2 materialPos,
  vec2 pointerMaterialPos,
  vec2 pointerVelocityLocal,
  float field
) {
  vec2 delta = materialPos - pointerMaterialPos;
  float cssToMaterial = 1.0 / max(uCssResolution.y * uZoom, 1.0);
  // The fragment shader is an inverse mapper: moving sample coordinates away
  // from the pointer contracts the visible ribbon. Contract the sampled domain
  // instead, which magnifies the visible center into an outward-facing dome.
  // Use an explicit optical magnification so geometry and dot scale share one
  // predictable value. The inverse lookup contracts by its reciprocal; the
  // fifth-order radial field rejoins untouched material without a shoulder.
  float lensScale = 1.0 / pointerLensMagnification(field);
  vec2 velocityDrag =
    pointerVelocityLocal * uPointerVelocityBiasPx * cssToMaterial * field;
  return pointerMaterialPos + delta * lensScale - velocityDrag;
}

float emissionLife(float seed, out float progress) {
  float lifetime = mix(uEmissionLifetime.x, uEmissionLifetime.y, seed);
  progress = fract(
    uTime / max(lifetime, 0.001) + hash11(uEmissionSeed + seed)
  );
  return
    smoothstep(0.0, 0.14, progress) *
    (1.0 - smoothstep(0.72, 1.0, progress));
}

float evaluateAuroraEmission(
  vec2 warpedPos,
  vec2 materialGridDerivativeX,
  vec2 materialGridDerivativeY,
  vec2 shapeNormal,
  float halftoneGranularity,
  float iterationProgress,
  vec2 pointerVelocityLocal,
  float pointerField,
  float dotMagnification,
  float emissionAnnulus
) {
  if (pointerField <= 0.001 || emissionAnnulus <= 0.001) return 0.0;

  vec2 materialPerCssPixel =
    vec2(1.0, uCrossAxisScale) /
    max(uCssResolution.y * uZoom, 1.0);
  float emission = 0.0;

  // Evaluate both contour sides. A deterministic side choice per source cell
  // keeps individual cells from duplicating while the aggregate emits from
  // both ribbon edges.
  for (int sideIndex = 0; sideIndex < 2; sideIndex++) {
    float side = sideIndex == 0 ? -1.0 : 1.0;
    float sideSalt = float(sideIndex) * 19.17;

    float guessCell = auroraHalftoneCellHash(
      warpedPos,
      iterationProgress + sideSalt * 0.0001,
      halftoneGranularity
    );
    float guessSeed = hash11(guessCell + uEmissionSeed + sideSalt);
    float guessProgress = 0.0;
    emissionLife(guessSeed, guessProgress);
    float guessTravel = mix(
      uEmissionPeelPx.x,
      uEmissionPeelPx.y,
      guessSeed
    ) * sin(guessProgress * 3.14159265359);
    vec2 guessVelocity =
      pointerVelocityLocal * uPointerVelocityBiasPx *
      materialPerCssPixel * guessProgress * 0.28;
    vec2 sourcePos =
      warpedPos - shapeNormal * side * guessTravel * materialPerCssPixel -
      guessVelocity;

    // Resolve the actual source cell after the first travel estimate, then
    // refine once. Radius remains a pure hash value and never depends on life.
    float sourceCell = auroraHalftoneCellHash(
      sourcePos,
      iterationProgress + sideSalt * 0.0001,
      halftoneGranularity
    );
    float sourceSeed = hash11(sourceCell + uEmissionSeed + sideSalt);
    float lifeProgress = 0.0;
    float lifeAlpha = emissionLife(sourceSeed, lifeProgress);
    float travel = mix(
      uEmissionPeelPx.x,
      uEmissionPeelPx.y,
      sourceSeed
    ) * sin(lifeProgress * 3.14159265359);
    vec2 velocityDrift =
      pointerVelocityLocal * uPointerVelocityBiasPx *
      materialPerCssPixel * lifeProgress * 0.28;
    sourcePos =
      warpedPos - shapeNormal * side * travel * materialPerCssPixel -
      velocityDrift;

    float dotHash = 0.0;
    vec2 cellCenter = vec2(0.0);
    float dot = auroraHalftoneDot(
      sourcePos,
      materialGridDerivativeX,
      materialGridDerivativeY,
      halftoneGranularity,
      iterationProgress + sideSalt * 0.0001,
      uEmissionRadiusScale * dotMagnification,
      uEmissionSoftnessScale * dotMagnification,
      dotHash,
      cellCenter
    );
    float sourceTolerance = 0.72 / halftoneGranularity;
    float sourceExists = 1.0 - smoothstep(
      sourceTolerance * 0.45,
      sourceTolerance,
      sceneShapeDistance(cellCenter)
    );
    float occupied = 1.0 - step(
      uEmissionOccupancy,
      hash11(dotHash + uEmissionSeed * 1.71)
    );
    float selectedSide = step(
      0.5,
      hash11(dotHash + uEmissionSeed * 2.37)
    );
    float sideMatches = sideIndex == 0 ? 1.0 - selectedSide : selectedSide;

    emission +=
      dot * sourceExists * occupied * sideMatches * lifeAlpha;
  }

  return clamp(
    emission * pointerField * emissionAnnulus * uEmissionOpacity,
    0.0,
    1.0
  );
}
`;
//# sourceMappingURL=interaction-shader.js.map